/**
 * Adapters that normalize scrub API DTOs into the shape the UI components
 * (QueuePage, EstimateDetailPage, PdfContent) already consume.
 *
 * Keeping the transformation here lets us evolve the API surface (or mock it)
 * without touching the render code.
 */

/** Coerce ISO strings, epoch seconds, and epoch milliseconds into a Date. The list API
 *  returns lastUpdated as an epoch number; detail returns updatedAt as ISO. Values < 1e12
 *  are treated as seconds (10-digit timestamps) and bumped to milliseconds. */
const toDate = (value) => {
  if (value == null || value === '') return null
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? null : d
  }
  // String: try numeric epoch first, then fall back to Date parsing of ISO/RFC strings.
  if (/^\d+$/.test(String(value).trim())) {
    const n = Number(value)
    const ms = n < 1e12 ? n * 1000 : n
    const d = new Date(ms)
    return Number.isNaN(d.getTime()) ? null : d
  }
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

const formatRelDate = (value) => {
  const d = toDate(value)
  if (!d) return ''
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
}

const toIsoOrNull = (value) => {
  const d = toDate(value)
  return d ? d.toISOString() : null
}

const normalizeStatus = (raw) => {
  if (!raw) return 'new'
  const s = String(raw).toLowerCase().replace(/_/g, ' ')
  if (s === 'reviewed') return 'reviewed'
  if (s === 'quote sent') return 'quote sent'
  if (s === 'processing' || s === 'pending' || s === 'running' || s === 'queued') return 'processing'
  if (s === 'failed' || s === 'error') return 'failed'
  return 'new'
}

export const adaptRecordRef = (dto) => {
  // The list/detail API attaches a downstream QWI reference once the estimate has been
  // linked. Canonical BE shape (ScrubEstimateService.loadLinkedQwiForOne / loadLinkedQwiForPage):
  //   linked_qwi: {
  //     qwi_id, latest_stage: 'QUOTE'|'WORKORDER'|'INVOICE',
  //     quote_id, quote_reference_number,
  //     workorder_id, workorder_reference_number,
  //     invoice_id, invoice_reference_number
  //   }
  // We pick the *_reference_number matching latest_stage for both the display label
  // (`Q #100891`) and the route, since `/quotes/:referenceNumber/summary` etc. take
  // the human reference number, not the internal qwi_id.
  const linked = dto.linked_qwi || dto.linkedQwi
  if (linked) {
    const stage = (linked.latest_stage || linked.latestStage || linked.stage || '').toUpperCase()
    if (stage === 'WORKORDER' || stage === 'WORK_ORDER') {
      const num = linked.workorder_reference_number || linked.workorderReferenceNumber
      if (num) return { type: 'WO', number: num, stage: 'WORKORDER', href: `/workorders/${num}/summary` }
    }
    if (stage === 'INVOICE') {
      const num = linked.invoice_reference_number || linked.invoiceReferenceNumber
      if (num) return { type: 'INV', number: num, stage: 'INVOICE', href: `/invoices/${num}/summary` }
    }
    if (stage === 'QUOTE') {
      const num = linked.quote_reference_number || linked.quoteReferenceNumber
      if (num) return { type: 'Q', number: num, stage: 'QUOTE', href: `/quotes/${num}/summary` }
    }
    // No stage match, but a reference number is present — pick whichever stage has one,
    // preferring the latest in the lifecycle (invoice > workorder > quote).
    const invNum = linked.invoice_reference_number || linked.invoiceReferenceNumber
    if (invNum) return { type: 'INV', number: invNum, stage: 'INVOICE', href: `/invoices/${invNum}/summary` }
    const woNum = linked.workorder_reference_number || linked.workorderReferenceNumber
    if (woNum) return { type: 'WO', number: woNum, stage: 'WORKORDER', href: `/workorders/${woNum}/summary` }
    const qNum = linked.quote_reference_number || linked.quoteReferenceNumber
    if (qNum) return { type: 'Q', number: qNum, stage: 'QUOTE', href: `/quotes/${qNum}/summary` }
  }

  // Legacy shapes: recordRef / record / top-level *_Number fields.
  const ref = dto.recordRef || dto.record || {}
  const id = ref.referenceNumber || ref.reference_number || ref.number || dto.workorderNumber || dto.quoteNumber || dto.invoiceNumber
  const rawType = (ref.stage || ref.type || ref.recordType || ref.record_type || '').toUpperCase()
  if (!id) return null
  if (rawType === 'WORKORDER' || rawType === 'WORK_ORDER' || dto.workorderNumber) {
    return { type: 'WO', number: id, stage: 'WORKORDER', href: `/workorders/${id}/summary` }
  }
  if (rawType === 'QUOTE' || dto.quoteNumber) {
    return { type: 'Q', number: id, stage: 'QUOTE', href: `/quotes/${id}/summary` }
  }
  if (rawType === 'INVOICE' || dto.invoiceNumber) {
    return { type: 'INV', number: id, stage: 'INVOICE', href: `/invoices/${id}/summary` }
  }
  return { type: rawType || '', number: id }
}

/** Map ScrubEstimateListItemDto → queue row shape used in QueuePage. */
/** Latest auto-quote job state from the BE ({status, outcome, runAfter, lastError, detail, inProgress}); null when none. */
export const adaptAutoQuote = (dto) => {
  const aq = dto?.autoQuote || dto?.auto_quote
  if (!aq || !aq.status) return null
  const status = String(aq.status).toUpperCase()
  const outcome = aq.outcome || null
  return {
    status,
    outcome,
    inProgress: aq.inProgress ?? aq.in_progress ?? (status === 'PENDING' || status === 'RUNNING'),
    error: aq.lastError || aq.last_error || null,
    // Not quoted because the vehicle's quote, work order or invoice (on any RO) is still open;
    // detail names that record, e.g. "This vehicle is already in progress on work order W-12 (RO 5512)".
    vehicleInProgress: outcome === 'SKIPPED_VIN_IN_PROGRESS',
    detail: aq.detail || null
  }
}

const STAGE_LABELS = { QUOTE: 'quote', WORK_ORDER: 'work order', INVOICE: 'invoice' }

/**
 * Sentence for a QUOTE_ALREADY_EXISTS (409) refusal from its existing_quote record — the vehicle is
 * still in progress on that quote, work order or invoice, on any RO. Same wording as the BE message;
 * null when the record is unknown (callers fall back to the BE's errorMessage).
 */
export const describeVehicleInProgress = (existing) => {
  if (!existing?.reference_number) return null
  const stage = STAGE_LABELS[existing.stage] || 'quote'
  const ro = existing.repair_order_num?.trim() ? ` (RO ${existing.repair_order_num.trim()})` : ''
  return `This vehicle is already in progress on ${stage} ${existing.reference_number}${ro}.`
}

export const adaptListItem = (dto) => {
  // calibrationCount (CAR-3844) is the count of ADAS calibrations only. Do not swap it for
  // calibrationFindings.requiredCalibrations: the BE fills that list with *every* finding,
  // safety operations included, so it overstates the count — it is only a fallback for a BE
  // that predates the field.
  const requiredCalibrations = dto.calibrationFindings?.requiredCalibrations
  const calibrationsCount = dto.calibrationCount ?? (Array.isArray(requiredCalibrations) ? requiredCalibrations.length : null)
  return {
    // Fall back across BE field-name variants so NEW/REVIEWED rows (which have
    // surfaced as missing-id from time to time) still produce a clickable detail link.
    id: dto.estimateId ?? dto.estimate_id ?? dto.id ?? null,
    ro: dto.roNumber,
    client: dto.client || '—',
    insurance: extractInsurance(dto) || '',
    vehicle: dto.vehicleModel || '—',
    make: dto.vinDetails?.make || dto.vinInfo?.make || '',
    vin: dto.vinDetails?.vin || dto.vin || '',
    // The list API (/v1/scrub-estimates?page=…&size=…) returns lastUpdated as an epoch
    // number — toDate handles the seconds-vs-ms ambiguity. createdAt/updatedAt are kept
    // as fallbacks if the BE shape ever changes.
    dateReceived: formatRelDate(dto.lastUpdated || dto.updatedAt || dto.createdAt),
    dateReceivedIso: toIsoOrNull(dto.lastUpdated || dto.updatedAt || dto.createdAt),
    status: normalizeStatus(dto.status),
    calibrationsCount,
    record: adaptRecordRef(dto),
    autoQuote: adaptAutoQuote(dto),
    estimateLocked: dto.estimateLocked === true,
    // Locked by the location's plan (monthly RO limit, 0173): findings and counts are left out until an upgrade.
    planLocked: dto.planLocked === true
  }
}

/** Group trigger lines by (sectionHeader, normalizedOperation) and collect their line numbers.
 *  Output: [{ sectionHeader, operation, lines: [14, 21, 26] }] sorted by min(lines).
 */
const groupTriggers = (triggerLines = []) => {
  const groups = new Map()
  triggerLines.forEach((t) => {
    if (!t || t.lineNumber == null) return
    const sectionHeader = (t.sectionHeader || '').trim()
    const operation = (t.normalizedOperation || t.operation || '').trim()
    const key = `${sectionHeader}__${operation}`
    if (!groups.has(key)) groups.set(key, { sectionHeader, operation, lines: [] })
    groups.get(key).lines.push(t.lineNumber)
  })
  return Array.from(groups.values())
    .map((g) => ({ ...g, lines: Array.from(new Set(g.lines)).sort((a, b) => a - b) }))
    .sort((a, b) => (a.lines[0] ?? 0) - (b.lines[0] ?? 0))
}

const formatTrigger = (group) => {
  const parts = []
  if (group.sectionHeader) parts.push(group.sectionHeader)
  if (group.operation) parts.push(group.operation)
  parts.push(`Line ${group.lines.join(', ')}`)
  return parts.join(' | ')
}

/** Format the BE `tool` field for display. The API may return a string or an array. */
const formatCalibrationSystem = (tool) => {
  if (!tool) return ''
  if (Array.isArray(tool)) return tool.filter(Boolean).join(' / ')
  return String(tool).trim()
}

/** Resolve the "Per {make}:" body text from the BE. The API surfaces the OEM reference under
 *  a few different keys depending on the cards's source — we accept all of them so a contract
 *  rename doesn't blank out the field. Falls back to oemSources (joined) and finally to the
 *  existing displayDocument string. */
const resolveOemReference = (card) => {
  const direct =
    card.oemReference ||
    card.oem_reference ||
    card.oemReferenceText ||
    card.oem_reference_text ||
    card.oemReferenceDocument ||
    card.oemPositionStatement ||
    card.oemRestriction
  if (direct) return String(direct).trim()
  if (Array.isArray(card.oemSources) && card.oemSources.length) return card.oemSources.filter(Boolean).join(', ')
  return card.displayDocument || ''
}

/** Normalize the OEM source documents attached to a card. Each entry carries a `filename`
 *  and a presigned S3 `url` (opened when the document is clicked). The label shown to the
 *  tech is the card-level `displayDocumentName` (resolved separately in adaptCard); the
 *  per-entry filename is the fallback. Entries without a URL are dropped. */
const adaptOemSourceLinks = (card) => {
  const raw = card.oemSourceLinks || card.oem_source_links || []
  if (!Array.isArray(raw)) return []
  return raw
    .map((link) => {
      if (!link) return null
      const url = link.url || link.s3Link || link.s3Url || ''
      const filename = link.filename || link.fileName || link.file_name || ''
      return { url, filename }
    })
    .filter((l) => l && l.url)
}

/** Map CalibrationCardDto → FindingCard item. All fallbacks are empty rather than fabricated. */
const adaptCard = (card, n, make) => {
  const triggers = groupTriggers(card.triggerLines)
  const triggeredBy = triggers.map(formatTrigger).join('\n')
  const oemNote = card.oemRestriction || (card.oemSources?.length ? card.oemSources.join(', ') : '')
  const oemReference = resolveOemReference(card)
  return {
    n,
    system: card.displayTitle || card.adasCategory || '',
    confidence: card.confidence ? card.confidence.charAt(0).toUpperCase() + card.confidence.slice(1).toLowerCase() : '',
    category: card.adasCategory || card.productType || '',
    calType: card.calibrationMethod || '',
    triggers,
    triggeredBy,
    raw: card,
    oemNote,
    location: card.location || '',
    calibrationSystem: formatCalibrationSystem(card.tool),
    oemReference,
    displayDocument: card.displayDocument || '',
    displayDocumentName: card.displayDocumentName || card.display_document_name || '',
    oemSourceLinks: adaptOemSourceLinks(card),
    make: make || '',
    firedRuleIds: Array.isArray(card.firedRuleIds) ? card.firedRuleIds.filter(Boolean) : [],
    deleted: !!(card.deleted ?? card.isDeleted ?? card.removed),
    deletedBy: card.deletedBy || card.removedBy || card.performedBy || '',
    deletedAt: card.deletedAt || card.removedAt || '',
    deletedReason: card.deletedReason || card.removedReason || card.reason || ''
  }
}

/** True when a Safety Operations card is the Pre and Post Repair Diagnostic Scan (PPRS).
 *  Per the locked contract (CAR-3511 IMP-011) PPRS is always pinned first in Safety
 *  Operations; Toyota + Rivian rule output regresses this so we re-pin on the FE. */
const isPrePostRepairScan = (item) => {
  const t = (item.system || '').toLowerCase()
  return t.includes('pre and post') && t.includes('diagnostic scan')
}

/** Split cards into ADAS Calibrations / Safety Operations / removed buckets. Removed cards
 *  (deleted=true on the DTO) are excluded from the active sections and surfaced separately
 *  so the page can render them in the dismissed drawer. */
const groupCards = (cards = [], make = '') => {
  const adas = []
  const safety = []
  const removed = []
  cards.forEach((card, i) => {
    const item = adaptCard(card, i + 1, make)
    if (item.deleted) {
      removed.push(item)
      return
    }
    const pt = (card.productType || '').toLowerCase()
    if (pt === 'diagnostic' || pt === 'service') safety.push(item)
    else adas.push(item)
  })
  // CAR-3511 IMP-011: pin PPRS to position 1 in Safety Operations, keeping every other
  // card in its original (backend) order — stable, and a no-op once the BE already leads
  // with PPRS.
  if (safety.length > 1) {
    safety.sort((a, b) => (isPrePostRepairScan(b) ? 1 : 0) - (isPrePostRepairScan(a) ? 1 : 0))
  }
  // Renumber sequentially across sections for display continuity
  const sections = []
  if (adas.length) sections.push({ section: 'ADAS Calibrations', items: adas })
  if (safety.length) sections.push({ section: 'Safety Operations', items: safety })
  let counter = 1
  sections.forEach((group) => group.items.forEach((item) => (item.n = counter++)))
  return { sections, removed }
}

/** Collect estimate lines from extractionResult into a flat, UI-ready list. */
const adaptEstimateLines = (extraction, triggerLineIndex) => {
  if (!extraction?.lineItems?.length) return []
  const out = []
  extraction.lineItems.forEach((group) => {
    const section = group.category || 'GENERAL'
    ;(group.lineItems || []).forEach((li) => {
      out.push({
        line: li.lineNumber,
        oper: li.rawOperation || li.operation || '',
        desc: [li.partName, li.notes].filter(Boolean).join(' — ') || li.operation || '',
        part: li.partNumber || '',
        qty: li.quantity ?? 1,
        price: li.price ?? 0,
        labor: li.laborHours ?? 0,
        paint: li.paintHours ?? 0,
        triggered: triggerLineIndex.has(li.lineNumber),
        section
      })
    })
  })
  return out.sort((a, b) => a.line - b.line)
}

/** Collect every line number mentioned as a trigger by any calibration card. */
const buildTriggerSet = (cards = []) => {
  const s = new Set()
  cards.forEach((card) => (card.triggerLines || []).forEach((t) => t?.lineNumber != null && s.add(t.lineNumber)))
  return s
}

/** Totals surfaced by the AnalyticalSummary (plus a safety-only count derived from cards). */
const adaptTotals = (dto) => {
  const summary = dto.summary || {}
  const byType = summary.findingsByType || {}
  const totalFindings = summary.totalFindings ?? dto.engineResponse?.calibrationCount ?? 0
  const safety = Object.entries(byType)
    .filter(([k]) => /safety|diagnostic|service/i.test(k))
    .reduce((acc, [, v]) => acc + v, 0)
  const oemRef = (dto.engineResponse?.cards || []).reduce((acc, c) => acc + (c.oemSources?.length || 0), 0)
  return {
    calibrations: dto.engineResponse?.calibrationCount ?? (summary.requiredCalibrations?.length || 0),
    safety,
    oemRef,
    findings: totalFindings
  }
}

/** Map ScrubEstimateDetailDto → full estimate shape used by EstimateDetailPage + PdfContent. */
export const adaptDetail = (dto) => {
  if (!dto) return null
  const vin = dto.vinInfo || {}
  const cards = dto.engineResponse?.cards || []
  // Trigger lines should be derived from active cards only (deleted ones don't contribute
  // to triggered estimate-line highlighting).
  const activeCards = cards.filter((c) => !(c?.deleted ?? c?.isDeleted ?? c?.removed))
  const triggerLineIndex = buildTriggerSet(activeCards)
  const vehicle = dto.vehicleModel || [vin.year, vin.make, vin.model, vin.trim].filter(Boolean).join(' ') || ''
  const grouped = groupCards(cards, vin.make)

  return {
    id: dto.estimateId,
    ro: dto.roNumber,
    client: dto.client || '—',
    clientId: dto.clientId ?? dto.client_id ?? null,
    vehicle,
    make: vin.make || '',
    vin: vin.vin || '—',
    vehicleImageUrl: vin.image_url || '',
    dateReceived: formatRelDate(dto.updatedAt || dto.createdAt || dto.lastUpdated),
    dateReceivedIso: toIsoOrNull(dto.updatedAt || dto.createdAt || dto.lastUpdated),
    // When this version was uploaded (never moves on later edits, unlike dateReceivedIso).
    createdAtIso: toIsoOrNull(dto.createdAt),
    status: normalizeStatus(dto.status),
    findingsSummary: `${dto.engineResponse?.calibrationCount ?? 0} cals`,
    record: adaptRecordRef(dto),
    autoQuote: adaptAutoQuote(dto),
    estimateLocked: dto.estimateLocked === true,
    planLocked: dto.planLocked === true,
    insurance: extractInsurance(dto) || '—',
    insuranceCompanyId: dto.insuranceCompanyId ?? dto.insurance_company_id ?? dto.extractionResult?.metadata?.insuranceCompanyId ?? null,
    claimNumber: extractClaim(dto) || '—',
    clientAddress: '',
    clientPhone: '',
    clientEmail: '',
    totals: adaptTotals(dto),
    installedSystems: (vin.safetySystems || [])
      .map((s) => s.feature_name)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' })),
    rawVehicle: vin, // raw vinInfo for downstream callers that need structured year/make/model/trim/vin
    findings: grouped.sections,
    removedCalibrations: grouped.removed,
    estimateLines: adaptEstimateLines(dto.extractionResult, triggerLineIndex),
    // Drives the "SCANNED" badge on the Estimate Summary page when the extraction
    // came from the LLM path rather than deterministic parsing.
    extractionSource: dto.extractionResult?.metadata?.extractionSource || '',
    // Drives the collision badge on the AI Report header. null whenever the BE omits
    // collisionAssessment, which keeps pre-scorer estimates rendering unchanged.
    collision: adaptCollisionAssessment(dto),
    version: dto.version,
    textSummary: dto.summary?.textSummary || '',
    requiredCalibrations: dto.summary?.requiredCalibrations || [],
    rawStatus: dto.status
  }
}

const extractInsurance = (dto) => {
  // Prefer the top-level insurance fields the list DTO now exposes; fall back to the
  // extractionResult metadata for older payload shapes / detail responses.
  const meta = dto.extractionResult?.metadata
  return (
    dto.insuranceCompany || dto.insurance_company || dto.insurance || meta?.insuranceCompany || dto.extractionResult?.insuranceCompany || ''
  )
}

const extractClaim = (dto) => {
  const meta = dto.extractionResult?.metadata
  return meta?.claimNumber || dto.extractionResult?.claimNumber || ''
}

// ─────────────────────── Collision assessment (CAR-3804) ───────────────────────

/** The BE's CollisionClassification enum, emitted on
 *  ScrubEstimateDetailDto.collisionAssessment.classification. `severity` drives the badge —
 *  'none' renders nothing, every other value renders a pill in the matching tone:
 *    COLLISION        weighted score reached the collision band → red.
 *    LIKELY_COLLISION mid band; the BE treats it as a collision but flags it for review,
 *                     so it badges amber rather than red.
 *    HAIL             pattern override (high PDR hours, negligible body/frame labor). Not a
 *                     collision, but badged so the header explains its own verdict instead
 *                     of going silent.
 *    NON_COLLISION    below the likely band → no badge.
 *  An unrecognised value is title-cased for display but never badged: better silent than
 *  mislabelling a verdict the FE hasn't been taught yet. */
const COLLISION_CLASSIFICATIONS = {
  COLLISION: { label: 'Collision', severity: 'high', isCollision: true },
  LIKELY_COLLISION: { label: 'Likely Collision', severity: 'medium', isCollision: true },
  HAIL: { label: 'Hail', severity: 'info', isCollision: false },
  NON_COLLISION: { label: 'Non-Collision', severity: 'none', isCollision: false }
}

/** Acronyms that must survive the SCREAMING_SNAKE → Title Case pass used on signal names
 *  (PDR_HOURS → "PDR Hours", not "Pdr Hours"). */
const COLLISION_ACRONYMS = new Set(['PDR', 'SRS', 'OEM', 'ADAS', 'RO', 'VIN'])

const titleCaseEnum = (raw) =>
  String(raw ?? '')
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => (COLLISION_ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ')

/** Trim the BE's trailing zeros so thresholds read as "2" / "2500" rather than "2.00". */
const formatSignalNumber = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)))
}

const adaptCollisionSignal = (signal) => {
  if (!signal) return null
  const parameter = signal.parameter || signal.name || ''
  if (!parameter) return null
  return {
    parameter,
    label: titleCaseEnum(parameter),
    threshold: signal.threshold ?? null,
    actual: signal.actual ?? null,
    weight: signal.weight ?? null,
    tripped: !!signal.tripped,
    scope: signal.scope || ''
  }
}

/** Map ScrubEstimateDetailDto.collisionAssessment → the badge model consumed by
 *  EstimateDetailPage. Returns null when the BE omits the object entirely (older
 *  estimates, or any response predating the collision scorer), so callers can render
 *  the header exactly as they did before. */
export const adaptCollisionAssessment = (dto) => {
  const raw = dto?.collisionAssessment || dto?.collision_assessment
  if (!raw) return null
  const classification = String(raw.classification || raw.result || '')
    .trim()
    .toUpperCase()
  if (!classification) return null

  const known = COLLISION_CLASSIFICATIONS[classification]
  const severity = known?.severity || 'none'
  const signals = (Array.isArray(raw.signals) ? raw.signals : []).map(adaptCollisionSignal).filter(Boolean)
  const score = Number.isFinite(Number(raw.score)) ? Number(raw.score) : null

  return {
    classification,
    label: known?.label || titleCaseEnum(classification),
    severity,
    // isCollision is the semantic verdict (COLLISION / LIKELY_COLLISION); showBadge is the
    // render decision, which also covers HAIL. They deliberately differ.
    isCollision: !!known?.isCollision,
    showBadge: severity !== 'none',
    score,
    signals,
    trippedSignals: signals.filter((s) => s.tripped)
  }
}

/** Human-readable explanation of a collision verdict, used as the badge's hover text:
 *    "Likely Collision · score 35 — Replace Collision Sections (6 ≥ 2), Total Cost (5404.01 ≥ 2500)"
 *  Falls back to a plain "no signals tripped" note so the tooltip is never empty. */
export const describeCollisionAssessment = (collision) => {
  if (!collision) return ''
  const head = [collision.label, collision.score != null ? `score ${formatSignalNumber(collision.score)}` : ''].filter(Boolean).join(' · ')
  if (!collision.trippedSignals.length) return `${head} — no signals tripped`
  const detail = collision.trippedSignals
    .map((s) => {
      const bounds =
        s.actual != null && s.threshold != null ? ` (${formatSignalNumber(s.actual)} ≥ ${formatSignalNumber(s.threshold)})` : ''
      return `${s.label}${bounds}`
    })
    .join(', ')
  return `${head} — ${detail}`
}
